import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Small pulsing badge showing the number of unread messages on a job.
 * Polls /api/jobs/{jobId}/messages/unread-count every 15s.
 *
 * Props:
 *  - jobId: string
 *  - refreshKey?: any — bump this (Date.now()) after marking-read to force an
 *                       immediate re-fetch. Useful when MessageBoard just
 *                       cleared the count on the server.
 */
export default function UnreadBadge({ jobId, refreshKey, className = "" }) {
  const [count, setCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/jobs/${jobId}/messages/unread-count`,
        { withCredentials: true }
      );
      setCount(Number(data?.count || 0));
    } catch {
      /* silent */
    }
  }, [jobId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  // Force a fresh fetch whenever a caller (e.g. MessageBoard onRead) bumps
  // refreshKey — after the mark-read POST completes.
  useEffect(() => {
    if (refreshKey !== undefined) load();
  }, [refreshKey, load]);

  if (!count) return null;
  return (
    <span
      data-testid={`unread-badge-${jobId}`}
      className={`relative inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 bg-[#0E93EC] text-white text-[11px] font-semibold leading-none rounded-full ${className}`}
    >
      <span className="absolute inset-0 rounded-full bg-[#0E93EC] animate-ping opacity-60" />
      <span className="relative">{count > 99 ? "99+" : count}</span>
    </span>
  );
}
